import React from 'react';
import { PieceOnPosition } from '../../hooks/useBoardState';

interface PathCellProps {
  position: number;
  colorClass: string;
  isHomeStretch: boolean;
  homeStretchColor?: string;
  isSafe: boolean;
  isHomeEntry: boolean;
  piecesOnPosition: PieceOnPosition[];
  validMoves: number[];
  onClick: () => void;
  onPieceClick: (
    e: React.MouseEvent,
    playerId: string,
    pieceIndex: number
  ) => void;
}

const PathCell: React.FC<PathCellProps> = ({
  position,
  colorClass,
  isHomeStretch,
  homeStretchColor,
  isSafe,
  isHomeEntry,
  piecesOnPosition,
  validMoves,
  onClick,
  onPieceClick,
}) => {
  const cellClass = `cell ${colorClass} ${isSafe ? 'safe' : ''}`;

  // Calculate arrow transform based on home entry position
  const getArrowTransform = () => {
    switch (position) {
      case 51: // Red
        return 'translateX(-2px) rotate(90deg)';
      case 12: // Green
        return 'translateY(-2px) rotate(180deg)';
      case 25: // Yellow
        return 'translateX(2px) rotate(270deg)';
      case 38: // Blue
        return 'translateY(2px) rotate(0deg)';
      default:
        return '';
    }
  };

  // Get arrow color based on position
  const getArrowColor = () => {
    switch (position) {
      case 51:
        return '#ff0000'; // Red
      case 12:
        return '#009900'; // Green
      case 25:
        return '#ffcc00'; // Yellow
      case 38:
        return '#66ccff'; // Blue
      default:
        return '#000';
    }
  };

  return (
    <div className={cellClass} onClick={onClick}>
      {/* Debug info - show position number */}
      <div
        style={{
          position: 'absolute',
          top: '2px',
          left: '2px',
          fontSize: '8px',
          color: '#666',
          pointerEvents: 'none',
        }}
      >
        {position >= 0 ? position : ''}
      </div>

      {/* Home entry arrows */}
      {isHomeEntry && (
        <div
          className="home-entry-arrow"
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: `translate(-50%, -50%) ${getArrowTransform()}`,
            width: '0',
            height: '0',
            borderLeft: '10px solid transparent',
            borderRight: '10px solid transparent',
            borderBottom: `16px solid ${getArrowColor()}`,
            zIndex: 5,
            opacity: 0.8,
            pointerEvents: 'none',
          }}
        />
      )}

      {/* Render pieces on this cell */}
      {piecesOnPosition.map((pieceInfo, index) => {
        const shouldHighlight = validMoves.includes(pieceInfo.pieceIndex);

        return (
          <div
            key={`${pieceInfo.player.id}-${pieceInfo.pieceIndex}`}
            className={`piece-on-board ${pieceInfo.player.color}${shouldHighlight ? ' highlighted' : ''}`}
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: `translate(-50%, -50%) translate(${index * 3}px, ${index * 3}px)`,
              width: '30px',
              height: '30px',
              borderRadius: '50%',
              border: '2px solid #000',
              zIndex: 10 + index,
              cursor: 'pointer',
            }}
            onClick={(e) =>
              onPieceClick(e, pieceInfo.player.id, pieceInfo.pieceIndex)
            }
          />
        );
      })}
    </div>
  );
};

export default PathCell;
